import React from 'react';
import i18next from 'i18next';
import styled, { css } from 'styled-components';

import { addMeasureType } from '~src/utils';

const Wrapper = styled.div<{ $width: string; $margin: string }>`
  margin: ${({ $margin }) => $margin};
  width: ${({ $width }) => $width};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProgressBarSC = styled.div<{
  $width: string;
  $height: string;
  $staticProgress?: boolean;
  $progress: number;
  $progressColor?: string;
}>(
  ({ $width, $height, theme, $staticProgress, $progress, $progressColor = theme.baseBlue.a10 }) => css`
    width: ${$width};
    height: ${$height};
    position: relative;
    background-color: ${$staticProgress ? theme.baseBlue.a1 : theme.baseBlue.a6};
    border-radius: 100px;
    overflow: hidden;

    > div {
      width: ${$progress}%;
      height: 100%;
      transition: width 1s;
      background-color: ${$progressColor};
      position: relative;
      overflow: hidden;
      border-radius: 100px;

      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        background-image: ${$staticProgress
          ? 'none'
          : `linear-gradient(
        -45deg,
        ${theme.white.a2} 25%,
        transparent 25%,
        transparent 50%,
        ${theme.white.a2} 50%,
        ${theme.white.a2} 75%,
        transparent 75%,
        transparent)`};
        z-index: 1;
        background-size: 50px 50px;
        animation: move 2s linear infinite;
        overflow: hidden;
      }
    }

    &.response-rate {
      width: 100%;
      height: 5px;
      background-color: ${theme.baseBlue.a3};

      > div {
        background-color: ${theme.green.a10};
        &::after {
          display: none;
        }
      }
    }

    > span::after {
      animation: move 2s linear infinite;
    }

    @keyframes move {
      0% {
        background-position: 0 0;
      }

      100% {
        background-position: 50px 50px;
      }
    }
  `,
);
const ProgressPercentageBox = styled.div`
  font-size: 0.8rem;
  text-transform: uppercase;
  margin-top: 10px;
  font-weight: 600;
`;

interface DefaultProps {
  width: number | string;
  height: number | string;
  margin: string;
  progress: number;
  className: string;
  staticProgress?: boolean;
  progressColor?: string | { [key: string]: string };
}

interface Props extends Partial<DefaultProps> {
  t?: i18next.TFunction;
  progressTxt?: string;
}

const ProgressBar: React.FC<Props> = React.memo(
  ({
    t,
    width = 200,
    height = 10,
    margin = '0',
    progress = 0,
    className = '',
    progressTxt,
    staticProgress,
    progressColor,
  }) => {
    if (typeof progressColor === 'object') {
      const currentPoint = Object.keys(progressColor)
        .filter(point => Number(point) < progress)
        .pop();
      progressColor = currentPoint ? progressColor[currentPoint] : undefined;
    }
    return (
      <Wrapper $width={addMeasureType(width)} $margin={margin}>
        <ProgressBarSC
          className={className}
          $staticProgress={staticProgress}
          $progressColor={progressColor}
          $width={addMeasureType(width)}
          $height={addMeasureType(height)}
          $progress={progress}
        >
          <div />
        </ProgressBarSC>
        {t && progressTxt && (
          <ProgressPercentageBox dat-translation={progressTxt}>
            {t(progressTxt)} {`${progress} %`}
          </ProgressPercentageBox>
        )}
      </Wrapper>
    );
  },
);

export default ProgressBar;
